import { useCallback, useEffect, useRef } from "react";
import { useReactFlow, useStoreApi } from "@xyflow/react";
import { nodeSize, toRect } from "../../core/rect";
import { revealShift } from "../../core/revealShift";
const REVEAL_DURATION_MS = 300;
/**
 * A next-selection reveal adopts the newly selected node; a reveal for a node
 * survives only while that node is the selection.
 */ function followSelection(armed, selectedNodeId) {
    if (armed?.nodeId === null) {
        return selectedNodeId ? {
            nodeId: selectedNodeId
        } : armed;
    }
    return armed?.nodeId === selectedNodeId ? armed : null;
}
function panBesidePanel(reactFlow, canvas, nodeId, panel) {
    const node = reactFlow.getInternalNode(nodeId);
    if (!node || !canvas) return;
    const viewport = reactFlow.getViewport();
    const topLeft = reactFlow.flowToScreenPosition(node.internals.positionAbsolute);
    // `measured` can be briefly unset after a structural rebuild.
    const { width, height } = nodeSize(node);
    const { dx, dy } = revealShift({
        ...topLeft,
        width: width * viewport.zoom,
        height: height * viewport.zoom
    }, toRect(panel.getBoundingClientRect()), toRect(canvas.getBoundingClientRect()));
    if (dx === 0 && dy === 0) return;
    void reactFlow.setViewport({
        ...viewport,
        x: viewport.x + dx,
        y: viewport.y + dy
    }, {
        duration: REVEAL_DURATION_MS
    });
}
/**
 * One smooth pan that moves a node into the free canvas beside the detail
 * panel, measured against the real panel and canvas rects. Zoom never changes.
 * Kept in refs: arming, consuming and the panel mounting never re-render the
 * editor.
 */ export function useDetailPanelReveal({ selectedNodeId, revealRequest }) {
    const reactFlow = useReactFlow();
    const store = useStoreApi();
    const panelRef = useRef(null);
    const armedRef = useRef(null);
    const selectedRef = useRef(selectedNodeId);
    const seenRequestIdRef = useRef(undefined);
    const frameRef = useRef(0);
    const tryReveal = useCallback(()=>{
        cancelAnimationFrame(frameRef.current);
        const panel = panelRef.current;
        const nodeId = selectedRef.current;
        if (!panel || !nodeId || armedRef.current?.nodeId !== nodeId) return;
        // A frame later: the panel ref attaches before react-rnd settles its
        // mount-time position.
        frameRef.current = requestAnimationFrame(()=>{
            armedRef.current = null;
            panBesidePanel(reactFlow, store.getState().domNode, nodeId, panel);
        });
    }, [
        reactFlow,
        store
    ]);
    const requestId = revealRequest?.id;
    const requestNodeId = revealRequest?.nodeId;
    useEffect(function followRevealTarget() {
        const requestChanged = requestId !== seenRequestIdRef.current;
        const selectionChanged = selectedNodeId !== selectedRef.current;
        seenRequestIdRef.current = requestId;
        selectedRef.current = selectedNodeId;
        if (requestChanged && requestNodeId !== undefined) {
            armedRef.current = {
                nodeId: requestNodeId
            };
        } else if (selectionChanged) {
            armedRef.current = followSelection(armedRef.current, selectedNodeId);
        }
        tryReveal();
        return ()=>cancelAnimationFrame(frameRef.current);
    }, [
        requestId,
        requestNodeId,
        selectedNodeId,
        tryReveal
    ]);
    const detailPanelRef = useCallback((panel)=>{
        panelRef.current = panel;
        if (panel) tryReveal();
        else cancelAnimationFrame(frameRef.current);
    }, [
        tryReveal
    ]);
    const revealNextSelection = useCallback(()=>{
        armedRef.current = {
            nodeId: null
        };
        return ()=>{
            armedRef.current = null;
            cancelAnimationFrame(frameRef.current);
        };
    }, []);
    return {
        detailPanelRef,
        revealNextSelection
    };
}

//# sourceMappingURL=useDetailPanelReveal.js.map